"use client";

import { Button } from "@/components/tailwind/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/tailwind/ui/dropdown-menu";
import { Input } from "@/components/tailwind/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/tailwind/ui/table";
import { useFolderContext } from "@/context/FolderContext";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { deleteDoc, doc, getDoc, getFirestore, updateDoc } from "firebase/firestore";
import {
  Book,
  Check,
  Clock,
  ExternalLink,
  FileIcon,
  Grid,
  List,
  MoreHorizontal,
  Pencil,
  Search,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

type ViewMode = "list" | "grid";
type TabType = "all" | "recent";

interface Document {
  id: string;
  name: string;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  updatedAt: any;
  owner: string;
}

interface TabButtonProps {
  icon: React.ReactNode;
  label: string;
  id: TabType;
  active: boolean;
  onClick: () => void;
}

function TabButton({ icon, label, active, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-colors ${
        active ? "bg-accent text-primary" : "bg-background border border-text-gray-500 hover:bg-accent"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

export function DocumentList() {
  const { activeFolderId } = useFolderContext();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  // Filter documents based on active tab and search query
  const filteredDocuments = useMemo(() => {
    let result = documents;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (doc) => doc.name.toLowerCase().includes(query) || doc.owner.toLowerCase().includes(query),
      );
    }

    // Apply tab filter
    if (activeTab === "recent") {
      result = [...result].sort(
        (a, b) => (b.updatedAt?.toDate()?.getTime() || 0) - (a.updatedAt?.toDate()?.getTime() || 0),
      );
    }

    return result;
  }, [documents, activeTab, searchQuery]);

  const handleRename = async (docId: string, newName: string) => {
    if (!newName.trim() || newName === documents.find((d) => d.id === docId)?.name) {
      setEditingId(null);
      return;
    }

    try {
      const db = getFirestore();
      const docRef = doc(db, "documents", docId);
      await updateDoc(docRef, { name: newName });

      setDocuments((docs) => docs.map((doc) => (doc.id === docId ? { ...doc, name: newName } : doc)));
      toast.success("Document renamed successfully");
    } catch (error) {
      console.error("Error renaming document:", error);
      toast.error("Failed to rename document");
    } finally {
      setEditingId(null);
    }
  };

  const startEditing = (docId: string, currentName: string) => {
    setEditingId(docId);
    setEditValue(currentName);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent, docId: string) => {
    if (e.key === "Enter") {
      handleRename(docId, editValue);
    } else if (e.key === "Escape") {
      setEditingId(null);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      const db = getFirestore();
      const docRef = doc(db, "documents", docId);
      await deleteDoc(docRef);

      // Remove from user's documents array
      if (!userId) {
        throw new Error("User ID is required to delete document");
      }
      const userDocRef = doc(db, "userCollections", userId);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const { documents: userDocs } = userDocSnap.data();
        await updateDoc(userDocRef, {
          documents: userDocs.filter((id: string) => id !== docId),
        });
      }

      // Remove from folder's documents array
      if (activeFolderId) {
        const folderRef = doc(db, "folders", activeFolderId);
        const folderSnap = await getDoc(folderRef);
        if (folderSnap.exists()) {
          const { documents: folderDocs = [] } = folderSnap.data();
          await updateDoc(folderRef, {
            documents: folderDocs.filter((id: string) => id !== docId),
          });
        }
      }

      setDocuments((docs) => docs.filter((doc) => doc.id !== docId));
      toast.success("Document deleted successfully");
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
    }
  };

  const handleOpenInNewTab = (docId: string) => {
    window.open(`/document/${docId}`, "_blank");
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUserId(null);
        setDocuments([]);
        return;
      }
      setUserId(user.uid);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!activeFolderId) {
      setDocuments([]);
      return;
    }

    const fetchDocs = async () => {
      const db = getFirestore();
      const folderRef = doc(db, "folders", activeFolderId);
      const folderSnap = await getDoc(folderRef);

      if (!folderSnap.exists()) {
        setDocuments([]);
        return;
      }

      const folderData = folderSnap.data();
      const docIds = folderData.documents || [];

      if (!Array.isArray(docIds) || docIds.length === 0) {
        setDocuments([]);
        return;
      }

      // Efficiently fetch all docs by ID
      const docSnaps = await Promise.all(docIds.map((id: string) => getDoc(doc(db, "documents", id))));

      // Collect unique ownerIds
      const ownerIds = Array.from(
        new Set(
          docSnaps
            .filter((snap) => snap.exists())
            .map((snap) => snap.data().ownerId)
            .filter(Boolean),
        ),
      );

      // Batch fetch userCollections for those ownerIds
      const userSnaps = await Promise.all(ownerIds.map((uid) => getDoc(doc(db, "userCollections", uid))));

      const ownerMap = Object.fromEntries(
        userSnaps.filter((snap) => snap.exists()).map((snap) => [snap.id, snap.data().username || "Unknown"]),
      );

      // Map documents with username as owner
      const docs = docSnaps
        .filter((snap) => snap.exists())
        .map((snap) => {
          const docData = snap.data();
          return {
            id: snap.id,
            name: docData.name || "Untitled",
            updatedAt: docData.updatedAt,
            owner: ownerMap[docData.ownerId] || "Unknown",
          };
        });

      setDocuments(docs);
    };

    fetchDocs();
  }, [activeFolderId]);

  if (!userId) return <div>Loading or not authenticated...</div>;

  return (
    <div className="space-y-4">
      {/* Header: Tabs, Search, Filter, View Mode */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-2">
          <TabButton
            icon={<Book size={16} />}
            label="All"
            id="all"
            active={activeTab === "all"}
            onClick={() => setActiveTab("all")}
          />
          <TabButton
            icon={<Clock size={16} />}
            label="Recently opened"
            id="recent"
            active={activeTab === "recent"}
            onClick={() => setActiveTab("recent")}
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <Input
              type="text"
              placeholder="Search my files"
              className="pl-9 w-64 h-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center border rounded-md">
            <Button variant="ghost" size="sm" className="h-9 px-3">
              <SlidersHorizontal size={16} />
              <span className="ml-2">Filter</span>
            </Button>
          </div>
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="icon"
              className="h-9 w-9 rounded-r-none border-r"
              onClick={() => setViewMode("list")}
            >
              <List size={16} />
            </Button>
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              size="icon"
              className="h-9 w-9 rounded-l-none"
              onClick={() => setViewMode("grid")}
            >
              <Grid size={16} />
            </Button>
          </div>
        </div>
      </div>

      {/* Table/List of Documents */}
      <div className="bg-background border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Name</TableHead>
              <TableHead>Modified</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Activity</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDocuments.map((doc) => (
              <TableRow
                key={doc.id}
                className="cursor-pointer hover:bg-muted"
                onClick={() => !editingId && router.push(`/document/${doc.id}`)}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-primary text-background flex items-center justify-center rounded">
                      <FileIcon size={14} />
                    </div>
                    <div className="flex-1">
                      {editingId === doc.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            ref={inputRef}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, doc.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-8"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRename(doc.id, editValue);
                            }}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingId(null);
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div className="font-medium">{doc.name}</div>
                          <div className="text-xs text-gray-500">{doc.owner}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{doc.updatedAt?.toDate().toLocaleDateString() ?? "Unknown"}</TableCell>
                <TableCell>{doc.owner}</TableCell>
                <TableCell>Recently Edited</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(doc.id, doc.name);
                        }}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        <span>Rename</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenInNewTab(doc.id);
                        }}
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        <span>Open in new tab</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(doc.id);
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>Delete</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
